import { NiraBusId, ScopedBus } from "types/busType";

/**
 * Saved favorites that the current view cannot resolve to a bus row.
 *
 * The panel's header count comes from the saved favorite ids, but its rows come
 * from a lookup pinned to the current region, scope and scope view. A favorite
 * with no `capacity_costs` row in that view resolves to nothing, so the two
 * legitimately disagree — a WECC bus favorited under discharging has no storage
 * results, for instance. Nothing was deleted; the lookup just came back empty.
 *
 * Compared as a set difference rather than by subtracting lengths. While a
 * fetch is in flight the rows are `previousData`, which lacks any favorite
 * starred since, so the gap is overstated until the fetch lands; the panel
 * hides it while loading for that reason.
 */
export const getUnresolvedFavorites = (
    favorites: ReadonlyArray<NiraBusId>,
    resolvedBuses: ReadonlyArray<ScopedBus>
): ReadonlyArray<NiraBusId> => {
    const resolvedIds = new Set(
        resolvedBuses.map((scopedBus) => scopedBus.bus.niraBusId)
    );
    return favorites.filter((niraBusId) => !resolvedIds.has(niraBusId));
};

/** "1 favorite has no results in this view" / "3 favorites have no results…" */
export const getUnresolvedFavoritesLabel = (count: number): string =>
    count === 1
        ? "1 favorite has no results in this view"
        : `${count} favorites have no results in this view`;

export const FAVORITES_PANEL_LIST = "list";
export const FAVORITES_PANEL_LOADING = "loading";
export const FAVORITES_PANEL_EMPTY_NO_FAVORITES = "emptyNoFavorites";
export const FAVORITES_PANEL_EMPTY_NO_RESULTS = "emptyNoResults";

export type FavoritesPanelView =
    | typeof FAVORITES_PANEL_LIST
    | typeof FAVORITES_PANEL_LOADING
    | typeof FAVORITES_PANEL_EMPTY_NO_FAVORITES
    | typeof FAVORITES_PANEL_EMPTY_NO_RESULTS;

/**
 * Which body the panel shows, keyed off the rows rather than the saved count.
 *
 * Keying off the saved count is what produced a blank panel under a non-zero
 * header: the list container rendered, then mapped an empty row list, so no
 * empty state ever appeared. Saved-but-unresolvable favorites get their own
 * state so the user learns why the list is short instead of doubting the count.
 *
 * "No results" is only claimed once the fetch has answered. Before that, the
 * first favorite in a region has no rows yet simply because nothing has been
 * fetched, and saying it has no results would be wrong.
 */
export const getFavoritesPanelView = (
    savedFavoritesCount: number,
    resolvedBusesCount: number,
    isLoading: boolean
): FavoritesPanelView => {
    if (resolvedBusesCount > 0) {
        return FAVORITES_PANEL_LIST;
    }
    if (savedFavoritesCount === 0) {
        return FAVORITES_PANEL_EMPTY_NO_FAVORITES;
    }
    return isLoading
        ? FAVORITES_PANEL_LOADING
        : FAVORITES_PANEL_EMPTY_NO_RESULTS;
};
